import {
  normalizeConnections,
  normalizeNodes
} from './data.js';
import {
  drawConnections,
  drawGrid,
  drawNodes
} from './drawing.js';
import {
  getCanvasContext,
  showEmptyState
} from './dom.js';
import { createDiagramInteractionHandlers } from './interactions.js';
import {
  createDiagramState,
  resizeDiagramViewport,
  syncDiagramCanvasSize
} from './viewport.js';

export function renderCanvasDiagram({
  canvas,
  wrapper = canvas.parentElement,
  statusChip = null,
  overlay = null,
  overlayTitle = null,
  overlayDesc = null,
  overlayTag = null,
  data = {}
}) {
  const ctx = getCanvasContext(canvas);
  if (!ctx || !wrapper) return null;

  const nodes = normalizeNodes(data.nodes, data);
  const connections = normalizeConnections(data.connections, nodes);

  if (nodes.length === 0) {
    showEmptyState(wrapper, statusChip);
    return {
      destroy() {}
    };
  }

  const state = createDiagramState(nodes);
  const resize = () => resizeDiagramViewport({ canvas, wrapper, ctx, nodes, state });
  const {
    handleClick,
    handleKeyDown,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp
  } = createDiagramInteractionHandlers({
    canvas,
    wrapper,
    statusChip,
    overlay,
    overlayTitle,
    overlayDesc,
    overlayTag,
    state
  });

  const draw = () => {
    syncDiagramCanvasSize({ canvas, wrapper, nodes, state, resize });

    const width = state.dimensions.width || canvas.clientWidth || wrapper.clientWidth || 960;
    const height = state.dimensions.height || canvas.clientHeight || wrapper.clientHeight || 500;

    ctx.clearRect(0, 0, width, height);
    drawGrid(ctx, state.offset, width, height);

    ctx.save();
    ctx.translate(state.offset.x, state.offset.y);
    ctx.scale(state.scale, state.scale);
    drawConnections(ctx, state.displayNodes, connections, state.frame);
    drawNodes(ctx, state.displayNodes, state.hoveredNode, state.selectedNode);
    ctx.restore();

    state.frame += 1;
    state.animationFrameId = window.requestAnimationFrame?.(draw) || null;
  };

  const start = () => {
    resize();
    draw();
  };

  wrapper.addEventListener('mousedown', handleMouseDown);
  wrapper.addEventListener('click', handleClick);
  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
  window.addEventListener('keydown', handleKeyDown);
  window.addEventListener('resize', resize);
  state.layoutFrameId = window.requestAnimationFrame?.(start) || null;

  return {
    ...state,
    destroy() {
      if (state.layoutFrameId) window.cancelAnimationFrame?.(state.layoutFrameId);
      if (state.animationFrameId) window.cancelAnimationFrame?.(state.animationFrameId);
      wrapper.removeEventListener('mousedown', handleMouseDown);
      wrapper.removeEventListener('click', handleClick);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', resize);
    }
  };
}
